import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import LoggingService from "../../../services/LoggingService";
import { useEffect, useState } from "react";
import {
  StripePaymentElementChangeEvent,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import {
  BookingSummary,
  BookingSummaryProps,
  Button,
  Description,
  Input,
  ReturnTableNotice,
} from "../../../components";
import {
  ButtonStyles,
  DescriptionStyles,
  DropdownStyles,
} from "../../../types/styles";
import styled from "styled-components";
import { VenueInfoProps } from "../../../components/VenueInfo/VenueInfo";
import { State } from "../../../types";
import { defaultThemeStyles } from "../../../mocks";
import { WidgetThemeStyles } from "../../../domain_objects/CrossSellSetupResponse";
import { useTranslation } from "react-i18next";

const StyledBookingSummary = styled(BookingSummary)`
  padding: 8px 0;
`;

const StyledDescriptionWrapper = styled.div`
  margin-bottom: 15px;
`;

const StyledForm = styled.form`
  display: grid;
  row-gap: 15px;
`;

const PoweredByStripeLogo = styled.img<{
  isDarkBackground?: boolean;
}>`
  content: ${({ isDarkBackground }) =>
    isDarkBackground
      ? "url(https://resdiary.blob.core.windows.net/widgetthemes/powered_by_stripe_white.svg)"
      : "url(https://resdiary.blob.core.windows.net/widgetthemes/Powered%20by%20Stripe%20-%20black.svg)"};
  height: 30px;
  padding-bottom: 12px;
  margin: 0 auto;
`;

export type StripePaymentElementProps = {
  makeBookingWithConfirmationTokenId: (
    confirmationTokenId: string,
    paymentIntentId?: string,
    setupIntentId?: string,
    stripeAuthCallbackFunc?: (
      confirmationTokenId: string,
      paymentIntentSecret?: string,
      setupIntentSecret?: string
    ) => void
  ) => void;
  handleStripeError: () => void;
  isCreditCardRequired: boolean;
  formattedPaymentAmount: string | undefined;
  hostname: string;
  restaurantId?: number;
  bookingSummary: BookingSummaryProps;
  isCrossSellChosen?: boolean;
  venueInfo: VenueInfoProps;
  styles: DropdownStyles & DescriptionStyles & ButtonStyles & WidgetThemeStyles;
  state: State;
};

export const StripePaymentElement: React.FC<StripePaymentElementProps> = ({
  makeBookingWithConfirmationTokenId,
  handleStripeError,
  isCreditCardRequired,
  formattedPaymentAmount,
  hostname,
  restaurantId,
  bookingSummary,
  isCrossSellChosen,
  venueInfo,
  styles,
  state,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const loggingService = new LoggingService();
  const [name, setName] = useState("");
  const [showNameField, setShowNameField] = useState(false);
  const [customerNameError, setCustomerNameError] = useState<string>("");
  const [isFormProcessing, setIsFormProcessing] = useState(false);
  const [isDarkBackground, setIsDarkBackground] = useState<boolean>(false);
  const { t } = useTranslation();

  // Handle the change event on PaymentElement
  const handlePaymentElementChange = (
    event: StripePaymentElementChangeEvent
  ) => {
    const paymentMethodType = event.value?.type; // Extract selected payment method
    setShowNameField(paymentMethodType === "card"); // Show name field only for cards
  };

  const handleSubmit = async (event: { preventDefault: () => void }) => {
    event.preventDefault();

    if (!stripe || !elements || isFormProcessing) {
      return;
    }

    setIsFormProcessing(true);

    // Validate name field
    if (!name && showNameField) {
      setCustomerNameError(t("CardNameRequired"));
      setIsFormProcessing(false);
      return;
    }

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setIsFormProcessing(false);
      return;
    }

    await createConfirmationToken()

    setIsFormProcessing(false);
  };

  const createConfirmationToken = async () => {
    if (!stripe || !elements) {
      return;
    }
    loggingService.logInfoMessage(
      {
        messageTemplate:
          "ProviderId {providerId} Widget Payment: calling Stripe createConfirmationToken...",
        messageParams: [restaurantId ? restaurantId.toString() : ""],
      },
      hostname
    );

    const { confirmationToken, error } = await stripe.createConfirmationToken({
      elements: elements,
      params: {
        payment_method_data: {
          billing_details: {
            name: name,
          },
        },
      },
    });

    if (error) {
      if (error.type !== "validation_error") {
        const paymentMethodId = error.payment_method
          ? error.payment_method.id
          : "none";
        loggingService.logErrorMessage(
          {
            messageTemplate: `ProviderId {providerId} Widget Payment: error from Stripe createConfirmationToken. Type - {errorType}. Code - {errorCode}. Decline code - {declineCode}. paymentMethod ID: ${paymentMethodId}`,
            messageParams: [
              restaurantId ? restaurantId.toString() : "",
              error.type,
              error.code ?? "",
              error.decline_code ?? "",
            ],
          },
          hostname
        );
        handleStripeError();
      }
    } else {
      loggingService.logInfoMessage(
        {
          messageTemplate: `ProviderId {providerId} Widget Payment: successfully created paymentMethod ${confirmationToken.id} from Stripe createConfirmationToken.`,
          messageParams: [restaurantId ? restaurantId.toString() : ""],
        },
        hostname
      );
      makeBookingWithConfirmationTokenId(
        confirmationToken.id,
        undefined,
        undefined,
        handleStripeAuth
      );
    }
  };

  const handleStripeAuth = async (
    confirmationTokenId: string,
    paymentIntentSecret?: string,
    setupIntentSecret?: string,
  ) => {
    if (!stripe) {
      return;
    }
    if (isCreditCardRequired && setupIntentSecret) {
      const { setupIntent, error } = await stripe.handleNextAction({ clientSecret: setupIntentSecret });
      if (error) {
        handleStripeError();
      } else {
        makeBookingWithConfirmationTokenId(confirmationTokenId, undefined, setupIntent?.id)
      }
    } else if (paymentIntentSecret) {
      const { paymentIntent, error } = await stripe.handleNextAction({ clientSecret: paymentIntentSecret });
      if (error) {
        handleStripeError();
      } else {
        makeBookingWithConfirmationTokenId(confirmationTokenId, paymentIntent?.id, undefined)
      }
    }
  };

  useEffect(() => {
    const colour = styles.OverallGeneralBackgroundColour;
    const m = colour.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
    const c_r = parseInt(m![1], 16);
    const c_g = parseInt(m![2], 16);
    const c_b = parseInt(m![3], 16);
    const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
    setIsDarkBackground(brightness < 125);
  }, [styles]);

  const options: StripePaymentElementOptions = {
    layout: {
      type: "tabs",
    },
    terms: {
      card: "never",
      applePay: "never",
      googlePay: "never",
    },
  };

  return (
    <StyledForm data-testid="payment-form" onSubmit={handleSubmit}>
      <StyledDescriptionWrapper>
        <Description styles={styles}>
          {isCreditCardRequired
            ? t("TokenizationOnly")
            : `${t("RequiredToPay")} ${formattedPaymentAmount} ${t("ToHoldThisBooking")}`}
        </Description>
      </StyledDescriptionWrapper>
      {showNameField && (
        <Input
          label={t("NameOnCard")}
          type="text"
          value={name}
          onChange={(val) => {
            setName(val.toString());
            if (val) setCustomerNameError("");
          }}
          errorMessage={customerNameError}
          required
          styles={styles}
          dataId="name-on-card"
        />
      )}
      <PaymentElement onChange={handlePaymentElementChange} options={options} />
      <PoweredByStripeLogo isDarkBackground={isDarkBackground} />
      <StyledBookingSummary
        {...bookingSummary}
        {...(isCrossSellChosen && {
          newProvider: {
            location: venueInfo.providerLocation,
            locationUrl: venueInfo.providerLocationUrl,
            name: venueInfo.providerName,
          },
        })}
      />
      {state.bookingDetails.timeSlot?.TimeSlot.IsLeaveTimeRequired &&
        state.bookingDetails.timeSlot?.TimeSlot.LeaveTime && (
          <ReturnTableNotice
            styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
            leaveTime={
              state.promotionDetails?.leaveTime.length
                ? state.promotionDetails.leaveTime
                : state.bookingDetails.timeSlot?.TimeSlot.LeaveTime
            }
            isPromotion={!!state.promotionDetails?.leaveTime.length}
          />
        )}
      <Button
        onClick={handleSubmit}
        styles={styles}
        dataId="confirm-booking-button"
      >
        {t("ConfirmBooking")}
      </Button>
    </StyledForm>
  );
};

export default StripePaymentElement;
